/**
 * Reverse-geocode helpers + client caller.
 *
 * Parsing is shared with the server proxy (`/api/geo/reverse`). The client
 * never hits Google's Geocoding REST API directly — referrer-restricted Maps
 * keys commonly return REQUEST_DENIED for browser geocode calls.
 */

export interface GoogleAddressComponent {
  long_name: string;
  short_name: string;
  types: string[];
}

export interface GoogleGeocodeResult {
  address_components: GoogleAddressComponent[];
  formatted_address: string;
}

export interface ReverseGeocodeOutput {
  stateName: string | null;
  districtName: string | null;
  city: string | null;
  /** 6-digit Indian pincode when Google returns `postal_code`. */
  pincode: string | null;
  formattedAddress: string | null;
}

/**
 * Walk Google Geocoding results and extract Indian admin + postal fields.
 * District falls back across level_2 → level_3 → locality because Google's
 * India results are inconsistent about which level holds the district.
 */
interface GeocodeFields {
  stateName: string | null;
  districtName: string | null;
  level3Name: string | null;
  city: string | null;
  pincode: string | null;
}

/** Fold a single address component into the running geocode field accumulator. */
function applyAddressComponent(fields: GeocodeFields, comp: GoogleAddressComponent): void {
  if (!fields.stateName && comp.types.includes('administrative_area_level_1')) {
    fields.stateName = comp.long_name;
  }
  if (!fields.districtName && comp.types.includes('administrative_area_level_2')) {
    fields.districtName = comp.long_name;
  }
  if (!fields.level3Name && comp.types.includes('administrative_area_level_3')) {
    fields.level3Name = comp.long_name;
  }
  if (!fields.city && (comp.types.includes('locality') || comp.types.includes('postal_town'))) {
    fields.city = comp.long_name;
  }
  if (!fields.pincode && comp.types.includes('postal_code')) {
    const digits = comp.long_name.replace(/\D/g, '');
    if (digits.length === 6) fields.pincode = digits;
  }
}

export function parseGoogleGeocodeResults(results: GoogleGeocodeResult[]): ReverseGeocodeOutput {
  const fields: GeocodeFields = {
    stateName: null,
    districtName: null,
    level3Name: null,
    city: null,
    pincode: null,
  };

  for (const result of results) {
    for (const comp of result.address_components) {
      applyAddressComponent(fields, comp);
    }
  }

  let { districtName } = fields;
  const { stateName, level3Name, city, pincode } = fields;

  // Prefer admin level_2, then level_3, then city/locality for district matching.
  // Skip "X Division" / "X Range" style admin names — those are rarely in our
  // district list (Google returns Bihar revenue divisions that way).
  const looksLikeDivision = (name: string | null) =>
    Boolean(name && /\b(division|range|region|commissionerate)\b/i.test(name));

  if (!districtName || looksLikeDivision(districtName)) {
    if (level3Name && !looksLikeDivision(level3Name)) districtName = level3Name;
    else if (city) districtName = city;
    else if (!districtName) districtName = level3Name;
  }

  return {
    stateName,
    districtName,
    city,
    pincode,
    formattedAddress: results[0]?.formatted_address ?? null,
  };
}

/**
 * Client reverse-geocode via our server proxy (never calls Google from the browser).
 */
export async function reverseGeocode(
  lat: number,
  lng: number,
  signal?: AbortSignal,
): Promise<ReverseGeocodeOutput> {
  const response = await fetch('/api/geo/reverse', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ lat, lng }),
    signal,
    cache: 'no-store',
  });

  const json = (await response.json().catch(() => ({}))) as ReverseGeocodeOutput & {
    error?: string;
  };

  if (!response.ok) {
    throw new Error(json.error || `Reverse geocode failed (${response.status})`);
  }

  return {
    stateName: json.stateName ?? null,
    districtName: json.districtName ?? null,
    city: json.city ?? null,
    pincode: json.pincode ?? null,
    formattedAddress: json.formattedAddress ?? null,
  };
}
